import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SubHeader from "components/common/SubHeader";
import { getUserPosts, blockUser, createReport } from "api/community";
import { getPublicUserKey, isSameUser, matchesUserParam } from "utils/userIdentity";
import { useSnackbar } from "contexts/SnackbarContext";
import useUserStore from "@store/useUserStore";
import PostMediaGrid from "components/community/PostMediaGrid";
import MediaViewer from "components/common/MediaViewer";
import BottomSheet from "components/common/BottomSheet";
import LoadingSpinner from "components/common/LoadingSpiner";
import useRefreshCallback from "hooks/useRefreshCallback";
import ProfileCard from "components/profile/ProfileCard";
import { getProfileV2 } from "api/user";
import { startDirectMessageRoom } from "api/chat";
import { MESSAGING_ENABLED } from "constants/featureFlags";
import "pages/community/community.scss";

const CommunityUserPosts = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { showError, showSuccess } = useSnackbar();
    const userInfo = useUserStore((state) => state.userInfo);
    const setUserInfo = useUserStore((state) => state.setUserInfo);
    const [posts, setPosts] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const observerRef = useRef(null);
    const bottomRef = useRef(null);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showReportSheet, setShowReportSheet] = useState(false);
    const [selectedReason, setSelectedReason] = useState(null);
    const [reportDescription, setReportDescription] = useState("");
    const [isReporting, setIsReporting] = useState(false);
    const [showBlockConfirm, setShowBlockConfirm] = useState(false);
    const [isBlocking, setIsBlocking] = useState(false);
    const [profileViewerOpen, setProfileViewerOpen] = useState(false);
    // 어떤 param 으로 로드한 프로필인지 함께 들고 있다가 렌더 시점에 스코프한다.
    // param 이 바뀐 직후 렌더에서 이전 사용자의 프로필로 본인 판정하면
    // 타인 프로필이 자기 URL 로 되튕긴다(상태를 비우는 것만으로는 같은 커밋에서 늦다).
    const [loadedProfile, setLoadedProfile] = useState({ key: null, data: null });
    const profile = loadedProfile.key === userId ? loadedProfile.data : null;
    // 공개 버킷리스트는 v2 통합 프로필 응답(bucket_list)에 포함 (별도 호출 제거)
    const bucketList = profile?.bucket_list ?? [];

    // legacy 내부 ID URL 로 들어오면 route param 만으로는 본인을 알 수 없다.
    // 로드된 프로필의 public_id 를 canonical 로 삼아 본인 판정을 보강한다.
    const canonicalPublicId = getPublicUserKey(profile);
    // route param 이 "세션이 스스로 들고 있는" 내부 식별자와 정확히 같으면 곧바로 자기 프로필이다.
    // 프로필 응답을 기다리지 않으므로, 응답이 늦거나 실패해도 자기 자신에게 메시지/신고/차단이
    // 노출되지 않는다. 이 판정은 화면 안에서만 쓰고 외부 URL 은 canonical public_id 로만 만든다.
    const isLegacySelfRoute = !!userInfo?.user_id && userInfo.user_id === userId;
    const isOwnProfile =
        matchesUserParam(userInfo, userId) ||
        isSameUser(profile, userInfo) ||
        isLegacySelfRoute;

    const REPORT_REASONS = [
        { value: 'spam', label: '스팸' },
        { value: 'harassment', label: '괴롭힘' },
        { value: 'hate_speech', label: '혐오 발언' },
        { value: 'violence', label: '폭력' },
        { value: 'inappropriate', label: '부적절한 콘텐츠' },
        { value: 'other', label: '기타' },
    ];

    // 유저 정보 — 프로필 v2 우선, 미응답 시 게시글에서 추출
    const user = posts[0]?.user || {};
    const authorNickname =
        profile?.nickname || posts[0]?.author_nickname || user.nickname || "사용자";
    const authorProfileImage =
        profile?.profile_image_url ||
        posts[0]?.author_profile_image ||
        user.profile_image ||
        "";

    const profileForCard = {
        nickname: authorNickname,
        name: profile?.name,
        profile_image_url: authorProfileImage,
        bio: profile?.bio,
        level: profile?.level,
        preferred_sport_name: profile?.preferred_sport_name,
        is_staff: profile?.is_staff ?? false,
        brand_name: profile?.brand_name,
        certificates: profile?.certificates ?? [],
        best_records: profile?.best_records ?? [],
    };

    const loadPosts = useCallback(
        async (pageNum, append = false) => {
            try {
                if (append) {
                    setIsLoadingMore(true);
                } else {
                    setIsLoading(true);
                    setPosts([]);
                }
                const result = await getUserPosts(userId, pageNum);
                if (result.success) {
                    setPosts((prev) =>
                        append ? [...prev, ...result.data] : result.data
                    );
                    setTotalPages(result.meta?.totalPages || 0);
                }
            } catch (error) {
                console.error("유저 게시글 로딩 실패:", error);
                showError("게시글을 불러오지 못했습니다.");
            } finally {
                setIsLoading(false);
                setIsLoadingMore(false);
            }
        },
        [userId, showError]
    );

    useEffect(() => {
        setPage(1);
        loadPosts(1);
    }, [loadPosts]);

    useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        getProfileV2(userId).then((data) => {
            if (!cancelled) setLoadedProfile({ key: userId, data });
        });
        return () => {
            cancelled = true;
        };
    }, [userId]);

    // 내 legacy URL 로 확인된 프로필 응답은 이 세션의 canonical public_id 를 담고 있다.
    // 세션에 채워 두지 않으면 URL 정규화 직후 param 이 UUID 로 바뀌면서
    // 본인 판정 근거(내부 식별자 일치)가 사라져 자기 프로필에 다시 타인용 액션이 뜬다.
    useEffect(() => {
        if (!isLegacySelfRoute || !canonicalPublicId) return;
        const latest = useUserStore.getState().userInfo;
        if (!latest?.user_id || latest.user_id !== userId || latest.public_id) return;
        setUserInfo({ ...latest, public_id: canonicalPublicId });
    }, [isLegacySelfRoute, canonicalPublicId, userId, setUserInfo]);

    // 자기 프로필이 legacy 내부 ID URL 이면 public_id URL 로 정규화한다.
    useEffect(() => {
        if (!isOwnProfile || !canonicalPublicId) return;
        if (canonicalPublicId === userId) return;
        navigate(`/community/user/${encodeURIComponent(canonicalPublicId)}`, {
            replace: true,
        });
    }, [isOwnProfile, canonicalPublicId, userId, navigate]);

    // Pull-to-Refresh
    useRefreshCallback(() => {
        setPage(1);
        return loadPosts(1);
    });

    // 무한 스크롤
    useEffect(() => {
        if (!bottomRef.current) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !isLoadingMore && page < totalPages) {
                    const nextPage = page + 1;
                    setPage(nextPage);
                    loadPosts(nextPage, true);
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(bottomRef.current);
        observerRef.current = observer;
        return () => observer.disconnect();
    }, [page, totalPages, isLoadingMore, loadPosts]);

    // 신고 제출
    const handleSubmitReport = useCallback(async () => {
        if (!selectedReason || isReporting) return;
        setIsReporting(true);
        try {
            const result = await createReport({
                target_type: 'user',
                target_id: userId,
                reason: selectedReason,
                description: selectedReason === 'other' ? reportDescription : undefined,
            });
            if (result.success) {
                showSuccess('신고가 접수되었습니다.');
            }
        } catch {
            showError('신고에 실패했습니다.');
        } finally {
            setIsReporting(false);
            setShowReportSheet(false);
            setSelectedReason(null);
            setReportDescription("");
        }
    }, [selectedReason, reportDescription, isReporting, userId, showSuccess, showError]);

    // 차단 실행
    const handleBlockUser = useCallback(async () => {
        if (isBlocking) return;
        setIsBlocking(true);
        try {
            const result = await blockUser(userId);
            if (result.success) {
                showSuccess('사용자를 차단했습니다.');
                navigate('/community');
            }
        } catch {
            showError('차단에 실패했습니다.');
        } finally {
            setIsBlocking(false);
            setShowBlockConfirm(false);
        }
    }, [userId, isBlocking, navigate, showSuccess, showError]);

    const handleStartMessage = useCallback(async () => {
        if (!userInfo) {
            navigate("/login");
            return;
        }
        try {
            const room = await startDirectMessageRoom(userId);
            if (room?.id) {
                navigate(`/messages/${encodeURIComponent(room.id)}`);
                return;
            }
            showError("메시지방을 시작하지 못했습니다.");
        } catch {
            showError("메시지방을 시작하지 못했습니다.");
        }
    }, [navigate, showError, userId, userInfo]);

    if (isLoading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="community-page community-list">
            <SubHeader title={`${authorNickname}의 프로필`} />

            <div className="community-profile-wrap">
                <ProfileCard
                    variant="community"
                    profile={profileForCard}
                    bucketList={bucketList}
                    onAvatarClick={
                        authorProfileImage
                            ? () => setProfileViewerOpen(true)
                            : undefined
                    }
                    onInstructorClick={
                        profile?.is_staff
                            ? () => navigate(`/instructor/${encodeURIComponent(userId)}`)
                            : undefined
                    }
                    trailingAction={
                        userInfo && !isOwnProfile ? (
                            <div className="community-profile-wrap__actions">
                                {MESSAGING_ENABLED && (
                                    <button
                                        type="button"
                                        className="community-profile-wrap__dm-btn"
                                        onClick={handleStartMessage}
                                    >
                                        메시지
                                    </button>
                                )}
                                <button
                                    className="community-profile-wrap__menu-btn"
                                    onClick={() => setShowUserMenu(true)}
                                    aria-label="사용자 메뉴"
                                >
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <circle cx="10" cy="4" r="1.5" fill="#757b80" />
                                        <circle cx="10" cy="10" r="1.5" fill="#757b80" />
                                        <circle cx="10" cy="16" r="1.5" fill="#757b80" />
                                    </svg>
                                </button>
                            </div>
                        ) : null
                    }
                />
            </div>

            {posts.length === 0 ? (
                <div className="empty-state">
                    <h3 className="empty-title">게시글이 없어요</h3>
                    <p className="empty-description">아직 작성한 게시글이 없습니다.</p>
                </div>
            ) : (
                <div className="post-list">
                    <PostMediaGrid posts={posts} />
                    <div ref={bottomRef} />
                    {isLoadingMore && <div className="loading-more">로딩 중...</div>}
                </div>
            )}

            {/* 유저 메뉴 (차단/신고) */}
            <BottomSheet
                isOpen={showUserMenu}
                onClose={() => setShowUserMenu(false)}
                height="auto"
            >
                <div className="post-action-menu">
                    <button
                        className="post-action-item"
                        onClick={() => {
                            setShowUserMenu(false);
                            setShowReportSheet(true);
                        }}
                    >
                        이 사용자 신고
                    </button>
                    <button
                        className="post-action-item delete"
                        onClick={() => {
                            setShowUserMenu(false);
                            setShowBlockConfirm(true);
                        }}
                    >
                        이 사용자 차단
                    </button>
                </div>
            </BottomSheet>

            {/* 신고 사유 선택 */}
            <BottomSheet
                isOpen={showReportSheet}
                onClose={() => {
                    setShowReportSheet(false);
                    setSelectedReason(null);
                    setReportDescription("");
                }}
                title="신고 사유 선택"
                height="auto"
            >
                <div className="report-reason-menu">
                    {REPORT_REASONS.map((r) => (
                        <button
                            key={r.value}
                            className={`report-reason-item${selectedReason === r.value ? ' report-reason-item--selected' : ''}`}
                            onClick={() => setSelectedReason(r.value)}
                        >
                            {r.label}
                            {selectedReason === r.value && (
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M3 8.5l3.5 3.5 6.5-7" stroke="#0066ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            )}
                        </button>
                    ))}
                    {selectedReason === 'other' && (
                        <textarea
                            className="report-description"
                            placeholder="상세 내용을 입력해주세요"
                            value={reportDescription}
                            onChange={(e) => setReportDescription(e.target.value)}
                            rows={3}
                        />
                    )}
                    <button
                        className="report-submit-btn"
                        disabled={!selectedReason || isReporting}
                        onClick={handleSubmitReport}
                    >
                        {isReporting ? '처리 중...' : '신고하기'}
                    </button>
                </div>
            </BottomSheet>

            {/* 차단 확인 */}
            <BottomSheet
                isOpen={showBlockConfirm}
                onClose={() => setShowBlockConfirm(false)}
                title="사용자 차단"
                height="auto"
            >
                <div className="block-confirm-menu">
                    <p className="block-confirm-message">
                        차단하면 이 사용자의 글이 보이지 않습니다. 차단하시겠습니까?
                    </p>
                    <div className="block-confirm-actions">
                        <button
                            className="block-confirm-cancel"
                            onClick={() => setShowBlockConfirm(false)}
                        >
                            취소
                        </button>
                        <button
                            className="block-confirm-submit"
                            disabled={isBlocking}
                            onClick={handleBlockUser}
                        >
                            {isBlocking ? '처리 중...' : '차단하기'}
                        </button>
                    </div>
                </div>
            </BottomSheet>

            {profileViewerOpen && authorProfileImage && (
                <MediaViewer
                    media={[{ url: authorProfileImage, type: 'image' }]}
                    initialIndex={0}
                    title={authorNickname}
                    onClose={() => setProfileViewerOpen(false)}
                />
            )}
        </div>
    );
};

export default CommunityUserPosts;
